import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import urlJoin from 'url-join';
import z from 'zod';
import { privateHttpClient } from '../httpClient';
import { createApiResponseSchema } from '../utils';
import { campaignsApiPath } from './apiPath';

const ProposalStatusSchema = z.enum(['WAITING', 'APPROVED', 'COMPLETED', 'REJECTED']);
const ProgressStatusSchema = z.enum(['UPCOMING', 'ONGOING', 'COMPLETED', 'CANCELED']);
const ProposalSortSchema = z.enum([
  'LATEST',
  'OLDEST',
  'BRAND_NAME',
  'HIGH_DISCOUNT',
  'MANY_GOODS',
  'ENDING_SOON',
]);

const GetProposalsRequestSchema = z
  .object({
    status: ProposalStatusSchema.optional(),
    progressStatus: ProgressStatusSchema.optional(),
    sort: ProposalSortSchema.optional(),
    page: z.number().min(1).optional(),
    size: z.number().min(1).max(100).optional(),
  })
  .describe('GetProposalsRequestSchema');

export type GetProposalsRequest = z.infer<typeof GetProposalsRequestSchema>;

const CouponResponseSchema = z
  .object({
    couponNo: z.number(),
    couponName: z.string(),
    couponKeyword: z.string(),
  })
  .describe('CouponResponseSchema');

const ProposalItemSchema = z
  .object({
    campaignProposalId: z.number(),
    brandId: z.string(),
    brandName: z.string(),
    logoUrl: z.string(),
    salesRate: z.number(),
    title: z.string(),
    status: ProposalStatusSchema,
    progressStatus: ProgressStatusSchema,
    couponQuantityLimit: z.number(),
    coupon: CouponResponseSchema.nullable().optional(),
    startAt: z.string(),
    endAt: z.string(),
  })
  .describe('ProposalItemSchema');

const GetProposalsResponseDataSchema = z.object({
  list: z.array(ProposalItemSchema),
});

const GetProposalsResponseSchema = createApiResponseSchema(GetProposalsResponseDataSchema);

export type GetProposalsResponse = z.infer<typeof GetProposalsResponseSchema>;

export const getProposals = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetProposalsRequestSchema),
    async (params: GetProposalsRequest): Promise<GetProposalsResponse> =>
      executeStandardRequest(async () => {
        const response = await privateHttpClient.get<GetProposalsResponse>(
          urlJoin(campaignsApiPath.getProposals),
          params,
        );
        return response;
      }),
    validateApiResponse(GetProposalsResponseSchema),
  ),
);
